import { useState } from 'react';
import { View, Pressable } from 'react-native';
import { useRouter } from 'expo-router';
import { FirebaseError } from 'firebase/app';
import { sendPasswordResetEmail } from 'firebase/auth';
import { Screen } from '@/components/layout/Screen';
import { Button } from '@/components/ui/Button';
import { InputField } from '@/components/ui/InputField';
import { Caption, ThemedText } from '@/components/ui/Text';
import { useTheme } from '@/theme';
import { auth } from '@/lib/firebase';
import { useAuthDraftStore } from '@/state/authDraftStore';
import { humanizeAsyncError } from '@/utils/asyncErrorMessage';

export default function ForgotPasswordScreen() {
  const router = useRouter();
  const theme = useTheme();
  const draftEmail = useAuthDraftStore((s) => s.email);
  const setDraftEmail = useAuthDraftStore((s) => s.setEmail);

  const [email, setEmail] = useState(draftEmail || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [sent, setSent] = useState(false);

  const handleSendReset = async () => {
    const trimmed = email.trim();
    if (!trimmed) {
      setError('Enter your email address to receive a reset link.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    setError(undefined);
    setSent(false);

    try {
      await sendPasswordResetEmail(auth, trimmed);
      setSent(true);
    } catch (err: unknown) {
      console.error('Password reset failed', err);
      if (err instanceof FirebaseError) {
        if (err.code === 'auth/user-not-found') {
          // Return the same UX as success to avoid account enumeration.
          setSent(true);
        } else if (err.code === 'auth/invalid-email') {
          setError('Please enter a valid email address.');
        } else if (err.code === 'auth/network-request-failed') {
          setError('Network request failed. Check your connection and try again.');
        } else if (err.code === 'auth/too-many-requests') {
          setError('Too many attempts. Please try again later.');
        } else {
          setError('We couldn’t send a reset link right now. Please try again.');
        }
      } else if (err instanceof Error) {
        setError(humanizeAsyncError(err, 'We couldn’t send a reset link right now. Please try again.'));
      } else {
        setError('We couldn’t send a reset link right now. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <Screen
        title="Check your email"
        subtitle="We sent a password reset link to the email you entered. Open the link to set a new password, then sign in."
        scrollable={false}
      >
        <View style={{ gap: theme.spacing(2) }}>
          <Caption color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(1) }}>
            If you don’t see the email, check your spam folder.
          </Caption>
          <Button
            label="Back to sign in"
            onPress={() => router.replace('/(onboarding)/email-auth')}
          />
          <Button
            label="Send another link"
            variant="ghost"
            onPress={() => {
              setSent(false);
              setError(undefined);
            }}
          />
        </View>
      </Screen>
    );
  }

  return (
    <Screen
      title="Reset password"
      subtitle="Enter the email for your Tripvio account and we’ll send you a link to set a new password."
      scrollable={false}
    >
      <View style={{ gap: theme.spacing(2) }}>
        <InputField
          label="Email"
          placeholder="you@example.com"
          keyboardType="email-address"
          autoCapitalize="none"
          autoCorrect={false}
          value={email}
          onChangeText={(text) => {
            setEmail(text);
            setDraftEmail(text);
            if (error) setError(undefined);
          }}
          errorText={error}
        />
        <Button label="Send reset link" onPress={handleSendReset} disabled={loading} />
        <Pressable
          onPress={() => router.back()}
          style={({ pressed }) => ({
            alignSelf: 'center',
            paddingVertical: theme.spacing(1),
            paddingHorizontal: theme.spacing(1.5),
            opacity: pressed ? 0.7 : 1,
          })}
          accessibilityRole="button"
          accessibilityLabel="Back to sign in"
        >
          <ThemedText variant="body" color={theme.colors.primary} style={{ textDecorationLine: 'underline' }}>
            Back to sign in
          </ThemedText>
        </Pressable>
      </View>
    </Screen>
  );
}
